import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Calculator, ShieldCheck, AlertTriangle, TrendingUp, Loader2, Users } from "lucide-react";
import api from "@/lib/api";

const fmt = (v) => {
  try { return `${Number(v).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`; }
  catch { return "—"; }
};

/**
 * Public read-only view of a saved simulator scenario. Accessed via
 * ``/scenario/:token`` where the token was generated from
 * ``POST /pricing/scenarios/{id}/share``. No authentication required.
 * If the token is revoked or invalid, shows a friendly 404.
 */
export default function PublicScenario() {
  const { token } = useParams();
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    api.get(`/public/scenario/${token}`)
      .then((r) => setScenario(r.data))
      .catch((e) => setError(e.response?.status === 404 ? "not_found" : "generic"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error === "not_found") {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center px-4" data-testid="public-scenario-notfound">
        <div className="max-w-md text-center bg-card rounded-2xl border border-border shadow-sm p-8">
          <div className="w-12 h-12 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-muted-foreground" />
          </div>
          <h1 className="font-heading text-lg text-foreground">Link inválido ou expirado</h1>
          <p className="text-sm text-muted-foreground mt-2">
            O cenário partilhado já não está disponível. Peça um novo link ao gestor que o criou.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center px-4">
        <div className="max-w-md text-center text-sm text-muted-foreground">
          Erro ao carregar o cenário. Tente novamente.
        </div>
      </div>
    );
  }

  const cfg = scenario?.config || {};
  const snap = cfg.snapshot || {};

  return (
    <div className="min-h-screen bg-muted/40 py-10 px-4" data-testid="public-scenario">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6 text-muted-foreground">
          <Calculator className="w-4 h-4 text-primary" />
          <div className="text-xs uppercase tracking-[0.2em] font-heading">Simulação Caring · partilhada</div>
        </div>

        <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-border bg-gradient-to-r from-card to-[hsl(var(--muted))]">
            <h1 className="font-display text-2xl text-foreground" data-testid="public-scenario-name">
              {scenario.name}
            </h1>
            {scenario.description && (
              <p className="text-sm text-muted-foreground mt-1" data-testid="public-scenario-desc">{scenario.description}</p>
            )}
            <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
              <Users className="w-3 h-3" />
              Criado por <b className="text-muted-foreground mx-1">{scenario.created_by_name || "—"}</b>
              {scenario.created_at && (
                <span> · {new Date(scenario.created_at).toLocaleDateString("pt-PT")}</span>
              )}
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* Service config */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading mb-2">Serviço</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/40 rounded-lg p-3 border border-border">
                  <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Modalidade</div>
                  <div className="font-heading text-sm text-foreground" data-testid="public-scenario-row">
                    {snap.row_name || "—"}
                  </div>
                </div>
                <div className="bg-muted/40 rounded-lg p-3 border border-border">
                  <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Configuração</div>
                  <div className="font-heading text-sm text-foreground">
                    {snap.days_per_week ?? "—"} × {snap.hours_per_day ?? "—"}h/dia
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing summary */}
            {snap.monthly_revenue !== undefined && (
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-card rounded-lg p-3 border border-border">
                  <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Preço/hora</div>
                  <div className="font-display text-xl text-foreground">{fmt(snap.hourly_price)}</div>
                </div>
                <div className="bg-card rounded-lg p-3 border border-border">
                  <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Mensal base</div>
                  <div className="font-display text-xl text-foreground">{fmt(snap.monthly_revenue_base)}</div>
                </div>

                {snap.multipliers?.length > 0 && (
                  <div className="col-span-2 bg-[hsl(var(--warning-bg))] rounded-lg p-3 border border-[hsl(var(--warning))]/25">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--warning))] font-heading mb-1">Factores multiplicadores</div>
                    <div className="space-y-1">
                      {snap.multipliers.map((m, i) => (
                        <div key={i} className="flex items-center justify-between text-sm text-[hsl(var(--warning))]">
                          <span>{m.name}</span>
                          <span className="font-mono">+{m.percentage}%</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-1 pt-1 border-t border-[hsl(var(--warning))]/25 flex items-center justify-between text-xs text-[hsl(var(--warning))]">
                      <span>Contribuição total</span>
                      <b className="font-mono">+{fmt(snap.multipliers_contribution)}</b>
                    </div>
                  </div>
                )}

                <div className="col-span-2 bg-white/10 text-white rounded-lg p-4">
                  <div className="text-[9px] uppercase tracking-[0.15em] text-white/60">Preço total ao cliente / mês</div>
                  <div className="font-display text-3xl mt-1" data-testid="public-scenario-total">{fmt(snap.monthly_revenue)}</div>
                </div>

                {snap.monthly_af_cost > 0 && (
                  <div className={`col-span-2 rounded-lg p-3 border ${snap.threshold_ok ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25" : "bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/25"}`}>
                    <div className="flex items-center gap-2">
                      {snap.threshold_ok
                        ? <ShieldCheck className="w-4 h-4 text-[hsl(var(--success))]" />
                        : <AlertTriangle className="w-4 h-4 text-[hsl(var(--danger))]" />}
                      <div className="flex-1">
                        <div className={`text-[10px] uppercase tracking-[0.15em] font-heading ${snap.threshold_ok ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"}`}>
                          Margem
                        </div>
                        <div className={`font-display text-xl ${snap.threshold_ok ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"}`}>
                          {snap.margin_pct?.toFixed(1)}% <span className="text-sm">/ min {snap.threshold_pct}%</span>
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          Absoluta: <b>{fmt(snap.monthly_absolute_margin)}</b>
                        </div>
                      </div>
                      <TrendingUp className={`w-5 h-5 ${snap.threshold_ok ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"}`} />
                    </div>
                    {snap.requires_authorization && (
                      <div className="mt-2 text-[12px] text-[hsl(var(--danger))] font-semibold">
                        Abaixo do mínimo — requere Autorização Especial.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-6 py-3 bg-muted/40 border-t border-border text-[10px] text-muted-foreground text-center">
            Cálculo indicativo · Catálogo Caring 2026 · thresholds 36/38/40 %
          </div>
        </div>
      </div>
    </div>
  );
}
